import React from 'react';
import styled from 'styled-components';
import { XDrawerBottom } from '../../../../x/drawer-bottom/XDrawerBottom';
import { UiFlex, UiIconMaterial, UiText, UiGrid } from '@open-stack/ui';
import { XTranslate } from '../../../../x/translate/XTranslate';
import { XInputPhone } from '../../../../x/input/phone/XInputPhone';
import { useImmer } from 'use-immer';
import { useApiQuery } from '@orderx/lab/xr/http';
import { apiMeOrderUpdatePost } from '../../../../../api/me.order-update.[post]';
import { toastShowSuccess } from '@orderx/lab/xr/toast';
import { Order } from '../../../../../XV2';
import { useCustomerOrders } from '../../../../../hooks/customer-orders';
import { XAlert, XAlertType } from '../../../../x/alert/XAlert';
import { useToggle } from '../../../../../hooks/toggle';
import { useUserSession } from '../../../../../hooks/user-session';
import { useMeta } from '../../../../../hooks/meta';
import { XButton, XText } from '@orderx/lab/ui';

const Wrapper = styled(UiGrid)`
    .header {
        &,
        .UiIconMaterial {
            font-size: var(--x-font-size-6);
        }
    }

    .phone-container {
        background-color: var(--x-color-surface);
        padding: var(--ui-spacing);
        border-radius: 40px;
    }
`;

interface Props {
    order: Order;
}

export function OrderViewDefaultContact({ order }: Props) {
    const orderId = order.id;
    const customerOrders = useCustomerOrders();
    const { isOrderIdContactFormHandled, setIsOrderIdContactFormHandled } = useUserSession();
    const hasPhone = Boolean(order.contactPhone);
    const { areaCode } = useMeta().meta;

    const [isContactFormOpen, toggleIsContactFormOpen] = useToggle(!hasPhone && !isOrderIdContactFormHandled(orderId));

    const [contactForm, setContactForm] = useImmer({
        isEdit: !hasPhone,
        areaCode: order.contactAreaCode || areaCode,
        phone: order.contactPhone,
    });

    const apiQueryUpdateContact = useApiQuery(async () => {
        await apiMeOrderUpdatePost({
            body: {
                action: 'UPDATE_CONTACT',
                orderId,
                areaCode: contactForm.areaCode,
                phone: contactForm.phone,
            },
        });

        await customerOrders.loadOrders();

        handleClose();
        toastShowSuccess('Contact info updated');
    });

    function handleClose() {
        setIsOrderIdContactFormHandled(orderId, true);
        toggleIsContactFormOpen(false);
    }

    return (
        <>
            <XAlert type={hasPhone ? XAlertType.DEFAULT : XAlertType.WARNING} onClick={toggleIsContactFormOpen}>
                <UiGrid>
                    {hasPhone ? (
                        <UiText>
                            We will send an SMS to{' '}
                            <UiText color="secondary" weight={2}>
                                +{order.contactAreaCode}
                                {order.contactPhone}
                            </UiText>{' '}
                            when you can come pick up products!
                        </UiText>
                    ) : (
                        <UiGrid>
                            <UiIconMaterial color="warning">warning</UiIconMaterial>

                            <span>
                                We're missing your phone number so we can't contact you when the order is ready. You
                                have 2 options:
                            </span>

                            <span>
                                1. Click here to enter your phone so we could let you know when your order is ready
                            </span>

                            <span>2. Track the order yourself by keeping an eye on this screen</span>
                        </UiGrid>
                    )}
                </UiGrid>
            </XAlert>

            {isContactFormOpen && (
                <XDrawerBottom onClose={handleClose}>
                    <Wrapper padding={4}>
                        <UiFlex className="header" gap={2}>
                            <UiIconMaterial>sms_failed</UiIconMaterial>

                            <UiText>
                                <XTranslate id="order_page.contact.title" />
                            </UiText>
                        </UiFlex>

                        <XTranslate id="order_page.contact.description" />

                        <UiFlex className="phone-container" gap={1}>
                            <XInputPhone
                                areaCode={contactForm.areaCode}
                                phone={contactForm.phone}
                                onChange={({ areaCode, phone }) => {
                                    setContactForm((form) => {
                                        form.areaCode = areaCode;
                                        form.phone = phone;
                                    });
                                }}
                            />

                            <XButton
                                onClick={apiQueryUpdateContact.run}
                                isLoading={apiQueryUpdateContact.isLoading}
                                color="primary"
                                className={'cta'}>
                                <XTranslate id="button.save" />
                            </XButton>
                        </UiFlex>

                        <XText color="text-secondary">
                            <XTranslate id="order_page.contact.additional_info" />
                        </XText>

                        <XButton onClick={handleClose} className={'cta'}>
                            No, thanks
                        </XButton>
                    </Wrapper>
                </XDrawerBottom>
            )}
        </>
    );
}
